import React from 'react';
import PropTypes from 'prop-types';
import {
  Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, IconButton, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import * as C from '../../../theme/colors.constants';
import { COLUNAS_PADRAO, COLUNAS_DETALHADA, formatarValorColuna } from './titulosController';

const COLUNAS_POR_MODO = {
  PADRAO: COLUNAS_PADRAO,
  DETALHADA: COLUNAS_DETALHADA,
};

function cellSx(campo, titulo) {
  if (campo === 'numero') {
    return { color: C.navy, fontWeight: 600 };
  }

  if (campo === 'atraso') {
    return { color: titulo.atraso > 90 ? C.tomato : C.black, fontWeight: 600 };
  }

  return undefined;
}

/**
 * Grid "Títulos do processo" (AW2-1034) — 2 conjuntos fixos de colunas
 * (`COLUNAS_PADRAO`/`COLUNAS_DETALHADA`, `titulosController.js`), escolhidos
 * pela prop `modoGrid` — alternados pelo botão único "Expandir/Recolher
 * Títulos" (Etapa 3, sem refetch: os 2 conjuntos leem do mesmo `titulos` já
 * carregado). Header e corpo iteram sobre o MESMO array de colunas (nunca
 * uma lista de `TableCell` hardcoded em paralelo) para nunca dessincronizar
 * de ordem, mesmo critério ao trocar de `modoGrid`. Ordenação (Etapa 2) via
 * `TableSortLabel` nas colunas com `ordenarPor` não-nulo — mesmo padrão de
 * `TableAntecedentesProcesso.jsx`. Paginação fica em `AbaAcompanhamento.jsx`
 * (`TablePagination`, fora do wrapper de scroll horizontal), mesmo padrão
 * de Antecedentes. Coluna "Ações" (AW2-1034 Etapa 4/5) com "Editar" e
 * "Excluir", mesma célula `AcoesCelula` abaixo.
 */
function AcoesCelula({
  titulo, totalLinhas, disabledGerenciarTitulos, cannotEnabled, onEditarTitulo, onExcluirTitulo,
}) {
  // `isTituloEditavel`/`isTituloExcluivel` NÃO são booleanos — são strings de
  // motivo de bloqueio devolvidas pelo backend (vazio/`null` = liberado,
  // texto = bloqueado com a razão). Confirmado inspecionando o JSON cru de
  // `GET {CORE}/processos/{id}/titulos` ao vivo (mesmo método já usado para
  // o bug de `descricaoTipoTitulo`/`idTipoTitulo` na Etapa 1): um título com
  // `isTituloEditavel: ""` (liberado) e `isTituloExcluivel: "O título não
  // pode ser excluído porque..."` (bloqueado) — o oposto do que a suposição
  // herdada do plano de arquitetura ("boolean, true=pode") assumia. Achado
  // AO VIVO, não em code review — as 2 etapas anteriores (4/5) usavam a
  // negação direta do campo (`!titulo.isTituloEditavel`), invertendo os 2
  // gates: título liberado aparecia com Editar desabilitado, e um título
  // explicitamente bloqueado para exclusão aparecia com Excluir habilitado.
  const motivoBloqueioEditar = titulo.isTituloEditavel || '';
  const motivoBloqueioExcluir = titulo.isTituloExcluivel || '';
  const desabilitarEditar = disabledGerenciarTitulos || cannotEnabled || Boolean(motivoBloqueioEditar);
  // `totalLinhas <= 1` replica a regra da tela real (`rows.length <= 1` em
  // `TabelaPesquisaTitulos.jsx`) — não pode excluir o único título restante
  // NA PÁGINA ATUAL (mesma semântica por página da tela real, não o total
  // de títulos do processo; replicado fielmente, não "corrigido" — decisão
  // já registrada no plano de arquitetura desta etapa).
  const desabilitarExcluir = disabledGerenciarTitulos || cannotEnabled
    || Boolean(motivoBloqueioExcluir) || totalLinhas <= 1;

  return (
    <>
      <Tooltip title={motivoBloqueioEditar.trim() || 'Editar'}>
        <span>
          <IconButton
            size="small"
            disabled={desabilitarEditar}
            onClick={() => onEditarTitulo(titulo)}
            aria-label="Editar título"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={motivoBloqueioExcluir.trim() || 'Excluir'}>
        <span>
          <IconButton
            size="small"
            disabled={desabilitarExcluir}
            onClick={() => onExcluirTitulo(titulo)}
            aria-label="Excluir título"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
}

export default function TableTitulosProcesso({
  titulos, ordenacao, onChangeOrdenacao, modoGrid,
  disabledGerenciarTitulos, cannotEnabled, onEditarTitulo, onExcluirTitulo,
}) {
  const colunas = COLUNAS_POR_MODO[modoGrid] ?? COLUNAS_PADRAO;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {colunas.map(coluna => (
            <TableCell key={coluna.campo} align={coluna.align}>
              {coluna.ordenarPor ? (
                <TableSortLabel
                  active={ordenacao.campo === coluna.ordenarPor}
                  direction={ordenacao.direcao.toLowerCase()}
                  onClick={() => onChangeOrdenacao(coluna.ordenarPor)}
                >
                  {coluna.label}
                </TableSortLabel>
              ) : coluna.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {titulos.map(t => (
          <TableRow key={t.id ?? `${t.sequencia}-${t.numero}`} hover>
            {colunas.map(coluna => (
              <TableCell key={coluna.campo} align={coluna.align} sx={cellSx(coluna.campo, t)}>
                {coluna.campo === 'acoes' ? (
                  <AcoesCelula
                    titulo={t}
                    totalLinhas={titulos.length}
                    disabledGerenciarTitulos={disabledGerenciarTitulos}
                    cannotEnabled={cannotEnabled}
                    onEditarTitulo={onEditarTitulo}
                    onExcluirTitulo={onExcluirTitulo}
                  />
                ) : formatarValorColuna(coluna.campo, t)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

AcoesCelula.propTypes = {
  titulo: PropTypes.shape({ isTituloEditavel: PropTypes.string, isTituloExcluivel: PropTypes.string }).isRequired,
  totalLinhas: PropTypes.number,
  disabledGerenciarTitulos: PropTypes.bool,
  cannotEnabled: PropTypes.bool,
  onEditarTitulo: PropTypes.func,
  onExcluirTitulo: PropTypes.func,
};

AcoesCelula.defaultProps = {
  totalLinhas: 0,
  disabledGerenciarTitulos: true,
  cannotEnabled: true,
  onEditarTitulo: null,
  onExcluirTitulo: null,
};

TableTitulosProcesso.propTypes = {
  titulos: PropTypes.array,
  ordenacao: PropTypes.shape({
    campo: PropTypes.string,
    direcao: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  onChangeOrdenacao: PropTypes.func,
  modoGrid: PropTypes.oneOf(['PADRAO', 'DETALHADA']),
  disabledGerenciarTitulos: PropTypes.bool,
  cannotEnabled: PropTypes.bool,
  onEditarTitulo: PropTypes.func,
  onExcluirTitulo: PropTypes.func,
};

TableTitulosProcesso.defaultProps = {
  titulos: [],
  ordenacao: { campo: 'dataVencimento', direcao: 'ASC' },
  onChangeOrdenacao: null,
  modoGrid: 'PADRAO',
  disabledGerenciarTitulos: true,
  cannotEnabled: true,
  onEditarTitulo: null,
  onExcluirTitulo: null,
};
